import { BrowserCookieStickyBucketService, GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useWindowSize } from 'react-use';
import { getSetKey, usePermissions, useRoles, useSubscriptionType } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { apiHost } from '@/lib/growthbook/config';
import { logEvent } from '@/pages/api/v1/utils';
import { buildAuthAttributes } from './authAttributes';
import { getDeviceId } from './getUUID';
import { resolveGrowthBookCountry } from './resolveGrowthBookCountry';
interface ABProviderProps {
  children: React.ReactNode;
}

// Function to get the current query string from the URL
const getQueryString = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  // Get the query string from the URL (without the leading ?)
  const search = window.location.search.slice(1);
  return search || '';
};
export const clientKey = process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY ?? 'sdk-B9LPZk9tXd5Mj4i3';
export const growthbook = new GrowthBook({
  apiHost,
  clientKey,
  enableDevMode: process.env.NEXT_PUBLIC_ENV !== 'production',
  trackingCallback: (experiment, result) => {
    try {
      logEvent({
        event_type: 'experiment_viewed',
        asPath: window.location.pathname,
        metadata: {
          experiment_id: experiment.key,
          variation_id: result.variationId
        }
      }).catch(error => {
        console.error('Failed to log experiment view:', error);
      });
    } catch (error) {
      console.error('Error in tracking callback:', error);
    }
  },
  stickyBucketService: new BrowserCookieStickyBucketService({
    jsCookie: Cookies
  }),
  streamingHost: 'https://cdn.growthbook.io'
});
void growthbook.init({
  streaming: true
});

// Expose the GrowthBook instance on `window` in non-production so E2E tests
// can force flags via setForcedFeatures(). Stripped in production builds.
if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_ENV !== 'production') {
  (window as unknown as {
    __growthbook?: typeof growthbook;
  }).__growthbook = growthbook;
}
function updateGrowthBookURL() {
  void growthbook.setURL(window.location.href);
}
export const ABProvider: React.FC<ABProviderProps> = ({
  children
}) => {
  const {
    settings
  } = useSettings();
  const router = useRouter();
  const {
    width,
    height
  } = useWindowSize();
  const rolesSet = useRoles();
  const rolesArray = Array.from(rolesSet);
  const rolesKey = getSetKey(rolesArray);
  const {
    permissions: permissionsSet
  } = usePermissions();
  const permissionsArray = Array.from(permissionsSet);
  const permissionsKey = getSetKey(permissionsArray);
  const subscriptionType = useSubscriptionType();
  useEffect(() => {
    if (!(settings && router.isReady)) {
      return;
    }

    // Set user attributes for targeting (from cookie, auth system, etc.)
    void growthbook.setAttributes({
      ...growthbook.getAttributes(),
      id: getDeviceId(),
      user_id: settings?.user.id,
      email: settings?.user.email,
      isGoogleUser: settings?.user.isGoogleUser ?? false,
      creditsCurrent: settings.credits.credits_current,
      isArtist: settings?.user.hasAccountArtist ?? false,
      isAdmin: settings?.user.hasAccountAdmin ?? false,
      pathname: router.asPath,
      query: getQueryString(),
      environment: process.env.NEXT_PUBLIC_ENV,
      country: resolveGrowthBookCountry(settings, Cookies.get('geo-country')),
      currency: settings?.currency.code,
      psPartnerKey: Cookies.get('ps_partner_key')
    });
  }, [settings, router.isReady, router.asPath, router.query]);

  // Set screen width and height
  useEffect(() => {
    void growthbook.setAttributes({
      ...growthbook.getAttributes(),
      screenWidth: width,
      screenHeight: height
    });
  }, [width, height]);

  // Set roles, permissions, and subscription type
  useEffect(() => {
    void growthbook.setAttributes({
      ...growthbook.getAttributes(),
      ...buildAuthAttributes(rolesArray, permissionsArray, subscriptionType)
    });
  }, [rolesKey, permissionsKey, subscriptionType]);
  useEffect(() => {
    router.events.on('routeChangeComplete', updateGrowthBookURL);
    return () => router.events.off('routeChangeComplete', updateGrowthBookURL);
  }, [router.events]);
  return <GrowthBookProvider growthbook={growthbook} data-sentry-element="GrowthBookProvider" data-sentry-component="ABProvider" data-sentry-source-file="index.tsx">{children}</GrowthBookProvider>;
};